import PropertySummaryCard from "@/components/PropertySummaryCard";
import SafeImage from "@/components/SafeImage";
import { Button } from "@/components/ui/button";
import UnderwriteSidebar from "@/components/UnderwriteSidebar";
import { getTotalRooms } from "@/lib/rooms";
import { Deal } from "@/types/deal";
import { Trash2, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import ExitStrategy from "@/modules/underwriting/ExitStrategy";
import FinancingStructure from "@/modules/underwriting/FinancingStructure";
import FnBRevenue from "@/modules/underwriting/FnBRevenue";
import InvestmentBudgetTable from "@/modules/underwriting/InvestmentBudgetTable";
import OperatingExpenses from "@/modules/underwriting/OperatingExpenses";
import OtherRevenue from "@/modules/underwriting/OtherRevenue";
import CashFlowStatement from "@/modules/underwriting/pages/CashFlowStatement";
import PLStatement from "@/modules/underwriting/pages/PLStatement";
import RampMacroSettings from "@/modules/underwriting/RampMacroSettings";
import RoomRevenue from "@/modules/underwriting/RoomRevenue";
import ChartsKPIsPage from "@/pages/ChartsKPIsPage";
import UnderwritingSummaryPage from "@/pages/UnderwritingSummaryPage";
import IntroductionPage from "./IntroductionPage";

import { deleteProject } from "@/lib/datasource";
import { normalizeFacilities } from "@/lib/facilities";
import {
  computeRoomsFromKpis,
  KpiValues,
  normalizeKpis,
  pickPurchasePrice,
} from "@/lib/kpis";
import type { PersistPayload } from "@/lib/persist/ProjectSaveContext";
import { ProjectSaveProvider } from "@/lib/persist/ProjectSaveContext";
import { formatCurrency, formatDate, lastSavedLabel } from "@/lib/utils";
import { plSnapshotFromKpis } from "@/modules/underwriting/pl/calc";

import { calculateFinancials } from "@/lib/kpis/calcCardKpis";
import { AccessDenied } from "@/components/summary/AccessDenied";
import PropertyDetailsFormNew from "@/modules/underwriting/PropertyDetailsFormNew";
import PayrollConfiguration from "@/modules/underwriting/PayrollModel";
import { useAuth } from "@/auth/AuthProvider";
import { loadAndHydrateDeal, logDealState } from "@/lib/helpers/dealWorkSpace";

export default function DealWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [deal, setDeal] = useState<Deal | null>(null);

  const dealRef = useRef<Deal | null>(deal);

  const [activeId, setActiveId] = useState("introduction");
  const [refreshToken, setRefreshToken] = useState(0);
  const [kpiUpdateKey, setKpiUpdateKey] = useState(0);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [projectId, setProjectId] = useState<string | undefined>(id);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    dealRef.current = deal;
  }, [deal]);

  useEffect(() => {
    if (!id) {
      navigate("/underwriting");
      return;
    }
    const load = async () => {
      setIsLoading(true);
      setAccessDenied(false);

      const loadedDeal = await loadAndHydrateDeal(id as string);

      if (!loadedDeal) {
        const canViewAll = user?.permissions?.view_all_projects === true;
        if (!canViewAll) {
          setAccessDenied(true);
        }
        setIsLoading(false);
        return;
      }

      setDeal(loadedDeal);
      setLastSaved(loadedDeal.updatedAt);
      setProjectId(loadedDeal.id);
      setIsLoading(false);
    };
    void load();
  }, [id, navigate, refreshToken, user]);

  const k = useMemo((): KpiValues => {
    if (!deal) {
      console.warn("[DealWorkspace] useMemo running with null deal.");
      return {};
    }

    const normalized = normalizeKpis(deal.kpis);
    const calculated = calculateFinancials(deal);

    const finalKpis = {
      ...normalized,
      ...calculated,
    };

    return finalKpis;
  }, [deal]);

  const totalRooms = computeRoomsFromKpis(k);
  const purchasePrice = pickPurchasePrice(k);
  const plY3 = plSnapshotFromKpis(k, 2);

  const buildPayload = useCallback((): PersistPayload => {
    const currentDeal = dealRef.current;

    if (!currentDeal) {
      console.warn("[buildPayload] called when deal is null");
      return { name: "Untitled Deal", kpis: null };
    }

    const rooms = getTotalRooms(currentDeal);
    const dealId = projectId || currentDeal.id || id;

    const gfaInput = (currentDeal.gfaSqm ??
      (currentDeal as any)?.buildingGfaSqm) as number | undefined;

    const facilitiesFromDeal = currentDeal.facilities || [];
    const facilitiesFromKpis = currentDeal.kpis?.facilities || [];
    const facilitiesFromProperty = currentDeal.kpis?.property?.facilities || [];

    const finalFacilities =
      facilitiesFromDeal.length > 0
        ? facilitiesFromDeal
        : facilitiesFromKpis.length > 0
          ? facilitiesFromKpis
          : facilitiesFromProperty;

    let kpis: any = {
      ...currentDeal.kpis,
      dealName: currentDeal.name,
      dealLocation: currentDeal.location,
      location: currentDeal.location,
      address: currentDeal.address || "",
      fullAddress: currentDeal.address || "",
      photoUrl: currentDeal.photoUrl || "",
      propertyType: currentDeal.propertyType,
      starRating: currentDeal.stars,
      stars: currentDeal.stars,
      currency: currentDeal.currency,
      gfaSqm: Number.isFinite(gfaInput)
        ? gfaInput
        : currentDeal.kpis?.gfaSqm || currentDeal.gfaSqm || 0,
      purchase: { price: currentDeal.purchasePrice || 0 },
      purchasePrice: currentDeal.purchasePrice,
      facilities: normalizeFacilities(finalFacilities),
      property: {
        ...(currentDeal.kpis?.property || {}),
        facilities: normalizeFacilities(finalFacilities),
      },
      roomMix: (currentDeal.roomTypes || []).map((rt) => ({
        id: rt.id || crypto.randomUUID(),
        type: rt.name,
        name: rt.name,
        count: rt.rooms,
        rooms: rt.rooms,
        adrWeight: rt.adrWeight || 100,
      })),
      rooms: { total: rooms },
    };

    // Room Revenue
    const roomModel = (currentDeal as any).roomRevenue;
    if (roomModel?.months?.length) {
      const to12 = (arr: any[]) => {
        const n = (arr ?? []).map((x) => Number(x ?? 0)).slice(0, 12);
        while (n.length < 12) n.push(0);
        return n;
      };
      const adr = to12(roomModel.months.map((m: any) => m.adr));
      const occ = to12(roomModel.months.map((m: any) => m.occPct));
      kpis.roomRev = { adr, occ };
      kpis.roomRevenue = {
        adr,
        occ,
        monthly: roomModel.months,
        totals: roomModel.totals,
        preset: roomModel.seasonalityPreset,
      };
      if (roomModel.totals) {
        kpis.avgADR = roomModel.totals.avgADR;
        kpis.avgOccupancy = roomModel.totals.avgOccPct;
        kpis.avgRevPAR = roomModel.totals.avgRevPAR;
        kpis.totalRoomsRevenue = roomModel.totals.roomsRevenue;
      }
    }

    // F&B Revenue
    if (currentDeal.fnbRevenue?.advanced) {
      const adv = currentDeal.fnbRevenue.advanced;
      kpis.fnb = {
        check: {
          guest: {
            breakfast: Number(adv.breakfast?.avgCheckGuest ?? 0),
            lunch: Number(adv.lunch?.avgCheckGuest ?? 0),
            dinner: Number(adv.dinner?.avgCheckGuest ?? 0),
            bar: Number(adv.bar?.avgCheckGuest ?? 0),
          },
          external: {
            breakfast: Number(adv.breakfast?.avgCheckExternal ?? 0),
            lunch: Number(adv.lunch?.avgCheckExternal ?? 0),
            dinner: Number(adv.dinner?.avgCheckExternal ?? 0),
            bar: Number(adv.bar?.avgCheckExternal ?? 0),
          },
        },
        capture: {
          breakfast: Number(adv.breakfast?.guestCapturePct ?? 0),
          lunch: Number(adv.lunch?.guestCapturePct ?? 0),
          dinner: Number(adv.dinner?.guestCapturePct ?? 0),
          bar: Number(adv.bar?.guestCapturePct ?? 0),
        },
        external: {
          perDay: {
            breakfast: Number(adv.breakfast?.externalCoversPerDay ?? 0),
            lunch: Number(adv.lunch?.externalCoversPerDay ?? 0),
            dinner: Number(adv.dinner?.externalCoversPerDay ?? 0),
            bar: Number(adv.bar?.externalCoversPerDay ?? 0),
          },
        },
        avgGuests: Number(
          currentDeal.fnbRevenue.simple?.avgGuestsPerOccRoom ?? 1.5,
        ),
      };
    } else if (currentDeal.kpis?.fnb) {
      kpis.fnb = currentDeal.kpis.fnb;
    }

    const budget = currentDeal.budget;
    if (budget) {
      kpis.budget = {
        other: Number(budget.otherDev) || 0,
        devFee: Number(budget.developmentFee) || 0,
        ffoeOse: Number(budget.ffeOse) || 0,
        profFees: Number(budget.professionalFees) || 0,
        dealCosts: Number(budget.dealCosts) || 0,
        preopening: Number(budget.preOpening) || 0,
        transferTax: Number(budget.reTransferTax) || 0,
        construction: Number(budget.constructionCosts) || 0,
        planningFees: Number(budget.planningCharges) || 0,
        contingencyPct: Number(budget.contingencyPct) || 10,
        insuranceAdmin: Number(budget.insuranceAdmin) || 0,
      };
      kpis.purchase = { price: Number(budget.netPurchasePrice) || 0 };
      kpis.purchasePrice = Number(budget.netPurchasePrice) || 0;
      kpis.totalInvestment = Number(budget.grandTotal) || 0;
      kpis.costPerRoom = rooms > 0 ? Number(budget.grandTotal) / rooms : 0;
      kpis.costPerSqm =
        currentDeal.gfaSqm > 0
          ? Number(budget.grandTotal) / currentDeal.gfaSqm
          : 0;
    } else if (currentDeal.kpis?.budget) {
      kpis.budget = currentDeal.kpis.budget;
    }

    if (currentDeal.kpis?.opex) kpis.opex = currentDeal.kpis.opex;

    if (currentDeal.payrollModel) {
      kpis.payrollModel = currentDeal.payrollModel;
    }

    if (currentDeal.kpis?.payroll) kpis.payroll = currentDeal.kpis.payroll;

    if (currentDeal.kpis?.financing)
      kpis.financing = currentDeal.kpis.financing;
    if (currentDeal.kpis?.ramp) kpis.ramp = currentDeal.kpis.ramp;
    if (currentDeal.kpis?.exit) kpis.exit = currentDeal.kpis.exit;
    if (currentDeal.kpis?.otherRev) kpis.otherRev = currentDeal.kpis.otherRev;

    if (currentDeal.kpis?.rampSettings) {
      kpis.rampSettings = currentDeal.kpis.rampSettings;
    }

    return {
      id: dealId,
      name: currentDeal.name,
      property_id: null,
      stage: "underwriting",
      currency: currentDeal.currency,
      kpis,
    };
  }, [id, projectId]);

  const handleAfterSave = (row: any) => {
    if (!projectId && row?.id) setProjectId(row.id);
    if (row?.updated_at) setLastSaved(row.updated_at);
    if (deal) {
      logDealState("After save completed", deal);
    }
  };

  const handleDelete = async () => {
    if (!deal || !deal.id) return;
    const dealName = deal.name || "this deal";
    toast(
      `Are you sure you want to delete "${dealName}"? This action cannot be undone.`,
      {
        id: "delete-confirmation",
        action: {
          label: "Delete",
          onClick: async () => {
            toast.dismiss("delete-confirmation");
            try {
              await deleteProject(deal.id);
              navigate("/underwriting");
              toast.success(`The deal "${dealName}" was successfully deleted.`);
            } catch (error) {
              console.error("Error deleting deal:", error);
              toast.error(`Failed to delete the deal "${dealName}".`);
            }
          },
        },
        onDismiss: () => toast.dismiss("delete-confirmation"),
      },
    );
  };

  const handleFormSaved = async () => {
    if (!deal || !deal.id) return;

    logDealState("1. Before form save (onSaved triggered)", deal);

    setRefreshToken((prev) => prev + 1);
    setKpiUpdateKey((prev) => prev + 1);
  };

  const handleFormSavedRamp = async () => {
    setRefreshToken((prev) => prev + 1);
    setKpiUpdateKey((prev) => prev + 1);

    if (deal) {
      logDealState("After Ramp form save (UI Refresh)", deal);
    }
  };

  const handleChildDealChange = (updatedDeal: Deal) => {
    setDeal(updatedDeal);
  };

  const AnimatedKpiCard = ({
    label,
    value,
    sub,
    tip,
  }: {
    label: string;
    value: string;
    sub?: string;
    tip?: string;
  }) => {
    return (
      <div
        key={kpiUpdateKey}
        className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card animate-in fade-in-0 duration-500"
      >
        <div className="flex items-start justify-between">
          <div className="text-sm font-medium text-navy-600">{label}</div>
          {kpiUpdateKey > 0 && (
            <TrendingUp className="h-4 w-4 text-green-500 animate-pulse" />
          )}
        </div>
        <div className="mt-2 text-2xl font-semibold text-navy-900 animate-in slide-in-from-bottom-2 duration-700">
          {value}
        </div>
        {sub && <div className="mt-1 text-xs text-navy-500">{sub}</div>}
        {tip && (
          <div className="mt-3 rounded-md bg-navy-50/50 p-2 text-xs text-navy-600">
            {tip}
          </div>
        )}
      </div>
    );
  };

  if (accessDenied) {
    return <AccessDenied />;
  }

  if (!deal) {
    return null;
  }

  return (
    <ProjectSaveProvider buildPayload={buildPayload} onSaved={handleAfterSave}>
      <div>
        <div className="mb-8">
          <div className="flex items-start gap-4 mb-4">
            {deal.photoUrl && (
              <button
                onClick={() => setShowPhotoLightbox(true)}
                className="flex-shrink-0 hover:opacity-80 transition-opacity"
              >
                <SafeImage
                  src={deal.photoUrl}
                  fallbackText={deal.name}
                  className="w-16 h-16 rounded-lg cursor-pointer"
                  alt={deal.name}
                  onClick={() => setShowPhotoLightbox(true)}
                  showMultipleIndicator={true}
                />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {deal.name || "Unnamed Deal"}
              </h1>
              <div className="text-sm text-slate-500 space-x-2">
                <span>ID: {deal.id ? deal.id.slice(0, 8) : "N/A"}</span>
                <span>•</span>
                <span>
                  Created:{" "}
                  {deal.createdAt ? formatDate(deal.createdAt) : "Unknown"}
                </span>
                <span>•</span>
                <span>
                  Last saved:{" "}
                  {lastSaved
                    ? lastSavedLabel(lastSaved)
                    : deal.updatedAt
                      ? lastSavedLabel(deal.updatedAt)
                      : "Never"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 text-slate-600 mb-4">
            <span>{deal.location || "Location not set"}</span>
            <span>•</span>
            <span>{totalRooms} rooms</span>
            <span>•</span>
            <span>{formatCurrency(purchasePrice, deal?.currency)}</span>
          </div>

          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate("/underwriting")}>
              ← Back to deals
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Deal
            </Button>
          </div>
        </div>

        <div className="mb-6 space-y-6">
          <PropertySummaryCard deal={deal} />

          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5 ">
            <AnimatedKpiCard
              label="# of Rooms"
              value={`${totalRooms} rooms`}
              sub={(() => {
                if (!k.roomMix) return "Room mix not set";

                let roomTypes: any[] = [];

                if (
                  typeof k.roomMix === "object" &&
                  "types" in k.roomMix &&
                  Array.isArray(k.roomMix.types)
                ) {
                  roomTypes = k.roomMix.types;
                } else if (Array.isArray(k.roomMix)) {
                  roomTypes = k.roomMix;
                }

                if (roomTypes.length === 0) return "Room mix not set";

                return roomTypes
                  .map(
                    (m: any) =>
                      `${m.name || m.type || "Room"} ${Math.round(
                        ((m.count || m.rooms || 0) / Math.max(1, totalRooms)) *
                          100,
                      )}%`,
                  )
                  .join(" • ");
              })()}
            />

            <AnimatedKpiCard
              label="Total Cost"
              value={
                k.totalInvestment
                  ? formatCurrency(k.totalInvestment, deal?.currency)
                  : "N/A"
              }
              sub="Total project investment"
            />

            <AnimatedKpiCard
              label="Year 3 Revenue"
              value={
                k.year3Revenue
                  ? formatCurrency(k.year3Revenue, deal?.currency)
                  : "N/A"
              }
              sub="Stabilized revenue"
            />

            <AnimatedKpiCard
              label="Year 3 Yield on Cost"
              value={
                k.year3YieldOnCost ? `${k.year3YieldOnCost.toFixed(2)}%` : "N/A"
              }
              sub="Year 3 EBITDA / Total Cost"
            />
          </div>
        </div>

        <div className="lg:hidden mb-4">
          <select
            className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm"
            value={activeId}
            onChange={(e) => setActiveId(e.target.value)}
          >
            <option value="introduction">Introduction</option>
            <option value="propertyDetails">Property Details</option>
            <option value="investmentBudget">Investment Budget</option>
            <option value="roomRevenue">Room Revenue</option>
            <option value="fbRevenue">F&B Revenue</option>
            <option value="otherRevenue">Other Revenue</option>
            <option value="payrollModel">Payroll Model</option>
            <option value="operatingCosts">Operating Costs</option>
            <option value="rampSettings">Ramp & Macro</option>
            <option value="financingStructure">Financing Structure</option>
          </select>
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="hidden lg:block">
            <UnderwriteSidebar
              dealId={deal.id}
              activeId={activeId}
              onSelect={setActiveId}
            />
          </div>

          <div className="space-y-6">
            {activeId === "introduction" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <IntroductionPage
                  onGetStarted={() => setActiveId("propertyDetails")}
                />
              </div>
            )}

            {activeId === "propertyDetails" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <PropertyDetailsFormNew
                  dealId={deal.id}
                  onSaved={handleFormSaved}
                />
              </div>
            )}

            {activeId === "investmentBudget" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <InvestmentBudgetTable
                  dealId={deal.id}
                  onSaved={handleFormSaved}
                />
              </div>
            )}

            {activeId === "roomRevenue" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <RoomRevenue
                  dealId={deal.id}
                  onSaved={handleFormSaved}
                  refreshKey={refreshToken}
                />
              </div>
            )}

            {activeId === "fbRevenue" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <FnBRevenue dealId={deal.id} onSaved={handleFormSaved} />
              </div>
            )}

            {activeId === "otherRevenue" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <OtherRevenue dealId={deal.id} onSaved={handleFormSaved} />
              </div>
            )}

            {activeId === "operatingCosts" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <OperatingExpenses dealId={deal.id} onSaved={handleFormSaved} />
              </div>
            )}

            {activeId === "payrollModel" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <div className="max-w-none w-full">
                  <PayrollConfiguration
                    initialData={deal?.kpis?.payroll || deal?.payrollModel}
                    rooms={totalRooms}
                    dataId={deal.kpis?.payroll.dataId}
                    propertyId={deal.id}
                  />
                </div>
              </div>
            )}

            {activeId === "rampSettings" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <RampMacroSettings
                  deal={deal}
                  onDealChange={handleChildDealChange}
                  onSaved={handleFormSavedRamp}
                />
              </div>
            )}

            {activeId === "financingStructure" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <FinancingStructure
                  dealId={deal.id}
                  onSaved={handleFormSaved}
                />
              </div>
            )}

            {activeId === "exitStrategy" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <ExitStrategy dealId={deal.id} onSaved={handleFormSaved} />
              </div>
            )}

            {activeId === "pl-statement" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <PLStatement />
              </div>
            )}

            {activeId === "cash-flow-statement" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <CashFlowStatement />
              </div>
            )}

            {activeId === "charts-kpis" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <ChartsKPIsPage />
              </div>
            )}

            {activeId === "underwriting-summary" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <UnderwritingSummaryPage />
              </div>
            )}
          </div>
        </div>

        {showPhotoLightbox && deal.photoUrl && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPhotoLightbox(false)}
          >
            <div className="max-w-4xl max-h-full">
              <img
                src={deal.photoUrl}
                alt={deal.name}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </ProjectSaveProvider>
  );
}
